import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8936 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8936: Clean Vehicle Credit Guide & Eligibility"
          description="Discover Form 8936 Clean Vehicle Credit eligibility and tax benefits for electric vehicles. Maximize your savings with TaxZerone's step-by-step guide."
          keywords=""
          canonical="resources/tax-exempt/form-8936-clean-vehicle-credit/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 8936,{' '}
          <span className="highlight-container">
            <span className="highlight">Clean Vehicle Credit </span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-8936-clean-vehicle-credit/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8936 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 8936? Form 8936, Clean Vehicle Credits,
                        </strong>{' '}
                        is used to calculate eligible{' '}
                        <strong style={{ fontWeight: 600 }}>
                          clean vehicle tax credits{' '}
                        </strong>
                        for qualifying vehicles placed in service during the tax
                        year. A separate{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Schedule A (Form 8936)
                        </strong>{' '}
                        is generally used for each qualifying vehicle.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses Form 8936?{' '}
                        </strong>{' '}
                        Individuals, partnerships, S corporations, and other
                        eligible taxpayers may use Form 8936 depending on the
                        type of clean vehicle credit being claimed. Partnerships
                        and S corporations must file Form 8936 to claim the
                        applicable credit.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Can Tax-Exempt Organizations Claim the Credit?
                        </strong>{' '}
                        Yes. An eligible tax-exempt organization may claim the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Qualified Commercial Clean Vehicle Credit through an
                          elective payment election.
                        </strong>{' '}
                        It generally files{' '}
                        <strong style={{ fontWeight: 600 }}> Form 8936,</strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-8936-clean-vehicle-credit/#scha"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Schedule A (Form 8936)
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-3800-business-credits/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 3800
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-T
                        </Link>{' '}
                        when making the election.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Credits Does Form 8936 Report?{' '}
                        </strong>
                        Form 8936 covers the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          New Clean Vehicle Credit, Previously Owned Clean
                          Vehicle Credit,{' '}
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Qualified Commercial Clean Vehicle Credit,
                        </strong>{' '}
                        subject to the applicable eligibility and acquisition
                        rules.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What's New for Form 8936?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 8936?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 8936?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howcan" sx={{ color: '#0202ff' }}>
                          How Can Tax-Exempt Entities Claim Clean Vehicle
                          Credits?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#line" sx={{ color: '#0202ff' }}>
                          Form 8936 Line-by-Line Filing Guide
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#clean" sx={{ color: '#0202ff' }}>
                          Schedule A (Form 8936) – Clean Vehicle Credit Amount
                        </Link>
                        <List>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#ischa" sx={{ color: '#0202ff' }}>
                                What Is Schedule A (Form 8936)?
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#whenis" sx={{ color: '#0202ff' }}>
                                When Is Schedule A Required?
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#info" sx={{ color: '#0202ff' }}>
                                Information Needed to Complete Schedule A
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#scha" sx={{ color: '#0202ff' }}>
                                Schedule A (Form 8936) - Instructions
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions on Form 8936
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What's New for Form 8936?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Clean Vehicle Credits for Vehicles Acquired{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    after September 30, 2025
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxpayers generally{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    cannot claim clean vehicle credits
                  </span>{' '}
                  for vehicles acquired after{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    September 30, 2025,
                  </span>{' '}
                  including:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }} component="ul">
                  <li>New clean vehicle credit.</li>
                  <li>Previously owned clean vehicle credit.</li>
                  <li>Qualified commercial clean vehicle credit.</li>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For purposes of Sections 25E, 30D, and 45W, a vehicle is
                  considered acquired when:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    A written binding purchase contract has been signed, and
                  </li>
                  <li>A payment has been made toward the vehicle. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A qualifying payment may include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>A cash down payment, </li>
                  <li>A nominal deposit, or </li>
                  <li>The value of a trade-in vehicle. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To qualify for a clean vehicle credit, taxpayers generally
                  must have entered into a binding purchase agreement and made a
                  qualifying payment on or{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    before September 30, 2025,
                  </span>{' '}
                  while also meeting all other IRS eligibility requirements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For additional guidance regarding these changes, is available
                  under{' '}
                  <span style={{ fontWeight: 600 }}> Public Law 119-21,</span>{' '}
                  which amended the rules for Sections 25E, 30D, and 45W.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is Form 8936?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8936, Clean Vehicle Credit,
                  </span>{' '}
                  allows taxpayers to claim credits for purchasing eligible
                  plug-in electric vehicles. Tax-exempt organizations use this
                  form to figure out the following credits for clean vehicles
                  you placed in service during your tax year.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      New Clean Vehicle Credit -
                    </span>{' '}
                    For eligible new clean vehicles that meet IRS requirements.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Previously Owned Clean Vehicle Credit -
                    </span>{' '}
                    For qualifying used clean vehicles purchased from a
                    registered dealer.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Qualified Commercial Clean Vehicle Credit -
                    </span>{' '}
                    For eligible clean vehicles used for business purposes.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In addition to Form 8936, taxpayers must generally complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule A (Form 8936){' '}
                  </span>{' '}
                  to determine the credit amount for each qualifying vehicle
                  before transferring the calculated credit to Form 8936.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Form 8936?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxpayers, both individuals and businesses, who purchase or
                  lease a qualifying plug-in electric drive motor vehicle during
                  the tax year must file <b>Form 8936</b> to claim the Clean
                  Vehicle Credit. This includes individuals, partnerships, S
                  corporations, and estates and trusts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  However, not every electric vehicle qualifies, so it’s
                  essential to confirm that the vehicle is eligible under the
                  current IRS guidelines before filing.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone for a filing experience that goes
                        beyond expectations.
                      </Typography>
                      <Typography variant="body2">
                        Empower your tax compliance journey with us today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8936`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howcan"
                >
                  How Can Tax-Exempt Entities Claim Clean Vehicle Credits?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Certain tax-exempt organizations and governmental entities
                  that do not typically benefit from federal income tax credits
                  may be eligible to receive the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Qualified Commercial Clean Vehicle Credit
                  </span>{' '}
                  through an elective payment process. This allows eligible
                  entities to treat the credit as a payment of income tax, which
                  may result in a refund if the credit exceeds any tax
                  liability.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                  Eligible entities generally include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Tax-exempt organizations </li>
                  <li>State, local, and territorial governments </li>
                  <li>Indian tribal governments </li>
                  <li>Alaska Native Corporations </li>
                  <li>Rural electric cooperatives </li>
                  <li>The Tennessee Valley Authority (TVA) </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To claim the credit, eligible entities must generally file:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <Link href="#scha" sx={{ color: '#0202ff' }}>
                      {' '}
                      Schedule A (Form 8936)
                    </Link>{' '}
                  </li>
                  <li>Form 8936 </li>
                  <li>
                    {' '}
                    <Link
                      href="/resources/tax-exempt/form-3800-business-credits/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 3800
                    </Link>{' '}
                  </li>
                  <li>
                    {' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-T
                    </Link>{' '}
                    or another applicable tax return{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before claiming the credit, entities must complete the IRS{' '}
                  <Link href="#common" sx={{ color: '#0202ff' }}>
                    pre-filing registration process
                  </Link>{' '}
                  and obtain a registration number. before electing payment of
                  the Qualified Commercial Clean Vehicle Credit. For additional
                  details, see IRC Section 6417 and related IRS guidance.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="line"
                >
                  Form 8936 Line-by-Line Filing Guide
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-clean-vehicle-img.webp" />
                  <source srcSet="/assets/images/f8936-clean-vehicle-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-clean-vehicle-img.png"
                    loading="lazy"
                    alt="Form 8936 Clean Vehicle Credits Line by Line instructions - Organization details"
                    width={760}
                    height={150}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before starting the line-by-line explanation on Form 8936,
                  it's important to understand the information displayed at the
                  top of the form.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Name(s) Shown on Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name(s) exactly as shown on the federal tax return
                  to which Form 8936 is attached.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Identifying Number
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the identifying number shown on the federal tax
                    return, such as a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Social Security Number (SSN)
                    </span>{' '}
                    or
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Employer Identification Number (EIN),
                    </span>{' '}
                    as applicable.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part I - Modified Adjusted Gross Income (MAGI) Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-part1-modified-img.webp" />
                  <source srcSet="/assets/images/f8936-part1-modified-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-part1-modified-img.png"
                    loading="lazy"
                    alt="Form 8936 Part I - Modified Adjusted Gross Income (MAGI) Amount Line 1a to Line 3e"
                    width={760}
                    height={228}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS uses your{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Modified Adjusted Gross Income (MAGI)
                  </span>{' '}
                  to determine whether you meet the income requirements for
                  claiming certain clean vehicle credits. In this section, you
                  will calculate your{' '}
                  <span style={{ fontWeight: 600 }}> MAGI</span> for both the
                  current tax year and the prior tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1a - Current Year Adjusted Gross Income
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Line 11 of your 2025 Form 1040, Form 1040-SR, or Form
                      1040-NR.{' '}
                    </span>
                  </li>
                  <li>
                    If you are filing as an estate or trust, enter the
                    applicable amount from{' '}
                    <span style={{ fontWeight: 600 }}> Form 1041</span> as
                    instructed by the IRS.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1b - Puerto Rico Income Exclusion
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any income from{' '}
                    <span style={{ fontWeight: 600 }}> Puerto Rico</span> that
                    was excluded from your gross income.
                  </li>
                  <li>
                    If you do not have excluded Puerto Rico income, enter{' '}
                    <span style={{ fontWeight: 600 }}> 0.</span>
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1c - Form 2555 Foreign Earned Income
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount reported on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 2555, Line 45.
                    </span>{' '}
                  </li>
                  <li>
                    This amount generally represents foreign-earned income and
                    certain housing exclusions or deductions claimed while
                    living and working abroad.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1d - Form 2555 Foreign Housing Deduction
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}> Form 2555, Line 50</span>
                    .{' '}
                  </li>
                  <li>
                    This amount relates to the foreign housing deduction claimed
                    on Form 2555.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1e - Form 4563 Exclusion Amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}> Form 4563, Line 15</span>
                    .
                  </li>
                  <li>
                    This figure applies to taxpayers who exclude income earned
                    in American Samoa.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Current Year Modified AGI
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Add the amounts entered on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Lines 1a through 1e
                    </span>{' '}
                    and enter the total on Line 2.{' '}
                  </li>
                  <li>
                    This total represents your{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      2025 Modified Adjusted Gross Income (MAGI),
                    </span>{' '}
                    which the IRS uses to determine your eligibility for clean
                    vehicle credits.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3a - Prior Year Adjusted Gross Income
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Line 11 of your 2024 Form 1040, Form 1040-SR, or Form
                      1040-NR
                    </span>
                    .
                  </li>
                  <li>
                    If you are filing as an estate or trust, enter the
                    applicable amount from{' '}
                    <span style={{ fontWeight: 600 }}> Form 1041</span> as
                    instructed by the IRS.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3b - Prior Year Puerto Rico Income Exclusion
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any income from{' '}
                  <span style={{ fontWeight: 600 }}> Puerto Rico</span> that was
                  excluded from your gross income during the prior tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3c - Prior Year Form 2555 Amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from Form 2555, Line 45, for the prior tax
                    year.
                  </li>
                  <li>
                    This includes foreign-earned income and certain
                    housing-related exclusions claimed for that year.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3d - Prior Year Foreign Housing Deduction
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>Form 2555, Line 50,</span>{' '}
                    for the prior tax year.
                  </li>
                  <li>
                    This amount reflects any foreign housing deduction claimed
                    during the prior year.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3e - Prior Year Form 4563 Exclusion Amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount from{' '}
                    <span style={{ fontWeight: 600 }}>Form 4563, Line 15,</span>{' '}
                    for the prior tax year.
                  </li>
                  <li>
                    This figure applies only if you excluded income earned in
                    American Samoa.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f8936-part1-modified-line4-img.webp" />
                  <source srcSet="/assets/images/f8936-part1-modified-line4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-part1-modified-line4-img.png"
                    loading="lazy"
                    alt="Form 8936 Part I - Modified Adjusted Gross Income (MAGI) Amount Line 4 to Line 5"
                    width={760}
                    height={175}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4 - Prior Year Modified AGI (MAGI)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts entered on{' '}
                  <span style={{ fontWeight: 600 }}> Lines 3a through 3e</span>{' '}
                  and enter the total on Line 4. This total represents your{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    prior-year Modified Adjusted Gross Income (MAGI)
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5 - 2024 Filing Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the abbreviation that corresponds to your filing status
                  for the{' '}
                  <span style={{ fontWeight: 600 }}> 2024 tax year</span>.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>S -</span> Single
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>MFS -</span> Married
                    Filing Separately{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>HOH -</span> Head of
                    Household{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>MFJ -</span> Married
                    Filing Jointly{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> QSS -</span> Qualifying
                    Surviving Spouse
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The filing status entered on this line determines the{' '}
                  <span style={{ fontWeight: 600 }}> MAGI limit</span> that
                  applies to your clean vehicle credit claim. To qualify, your
                  MAGI must not exceed the applicable income threshold for
                  either the current tax year or the prior tax year.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Filing Status
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            MAGI Limit
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Single (S)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $150,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Married Filing Separately (MFS)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $150,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Head of Household (HOH)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $225,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Married Filing Jointly (MFJ)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $300,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Qualifying Surviving Spouse (QSS)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $300,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Estates and Trusts
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center' }}>
                            $150,000
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part II - Credit for Business/Investment Use Part of New Clean
                  Vehicles
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-part2-credit-img.webp" />
                  <source srcSet="/assets/images/f8936-part2-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-part2-credit-img.png"
                    loading="lazy"
                    alt="Form 8936 Part II - Credit for Business/Investment Use Part of New Clean Vehicles Line 6 to Line 8"
                    width={760}
                    height={80}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Credit From Schedule A (Form 8936)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total credit amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part II of all applicable Schedule A (Form 8936)
                  </span>{' '}
                  forms. If you are claiming a credit for more than one
                  qualifying vehicle used for business or investment purposes,
                  combine the amounts from each Schedule A and enter the total
                  on this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7 - Credit From Partnerships and S Corporations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any new clean vehicle credits passed through to you from
                  a partnership or S corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You can find these amounts on:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, '& li': { mb: 1.2, fontWeight: 600 } }}
                >
                  <li>Schedule K-1 (Form 1065),</li>
                  <li>Schedule K-1 (Form 1120-S)</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Partnerships and S corporations should report this amount on
                  Schedule K. All other taxpayers should report this amount on
                  <Link
                    href="/resources/tax-exempt/form-3800-business-credits/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 3800
                  </Link>
                  , Part III, Line 1y.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8 - Business or Investment Use Credit
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Add <span style={{ fontWeight: 600 }}> Lines 6 and 7</span>{' '}
                    and enter the total on Line 8.{' '}
                  </li>
                  <li>
                    This amount represents your total{' '}
                    <span style={{ fontWeight: 600 }}>
                      business or investment use portion of the New Clean
                      Vehicle Credit.
                    </span>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part III - Credit for Personal Use Part of New Clean Vehicles
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-part3-credit-img.webp" />
                  <source srcSet="/assets/images/f8936-part3-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-part3-credit-img.png"
                    loading="lazy"
                    alt="Form 8936 Part III - Credit for Personal Use Part of New Clean Vehicles Line 9 to Line 13"
                    width={760}
                    height={129}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tip:</b> If you are claiming a credit for the personal-use
                  portion of a new clean vehicle, you must complete{' '}
                  <span style={{ fontWeight: 600 }}>Part II</span>, even if the
                  vehicle was not used for business or investment purposes.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Personal Use Credit Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total credit amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part III of all applicable Schedule A (Form 8936)
                  </span>{' '}
                  forms. If you are claiming credits for more than one
                  qualifying vehicle, combine the amounts and enter the total on
                  this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Tax Liability
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 1040, Form 1040-SR, or Form 1040-NR, Line 18
                  </span>
                  . This represents your total tax liability before applying the
                  clean vehicle credit.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11 - Personal Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total of the applicable personal credits reported on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule 3 (Form 1040), Lines 1 through 4, 5b, 6d, 6I, and
                    6m
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 12 - Available Tax Liability
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}> Line 11</span>{' '}
                    from <span style={{ fontWeight: 600 }}> Line 10</span> and
                    enter the result.{' '}
                  </li>
                  <li>
                    If the result is zero or less, enter{' '}
                    <span style={{ fontWeight: 600 }}> 0</span> and stop here.
                    You cannot claim the personal use portion of the clean
                    vehicle credit.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13 - Personal Use Part of Credit
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the smaller value between{' '}
                    <span style={{ fontWeight: 600 }}> Line 9</span> and{' '}
                    <span style={{ fontWeight: 600 }}> Line 12</span>. This is
                    the allowable personal use portion of your new clean vehicle
                    credit.{' '}
                  </li>
                  <li>
                    Also enter this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule 3 (Form 1040), Line 6f
                    </span>
                    .{' '}
                  </li>
                  <li>
                    If the amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 12</span> is less
                    than the amount on{' '}
                    <span style={{ fontWeight: 600 }}>Line 9</span>, you cannot
                    claim the excess credit. Any unused portion of the personal
                    clean vehicle credit is permanently lost and cannot be
                    carried back or carried forward to another tax year.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IV - Credit for Previously Owned Clean Vehicles
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-part4-personal-img.webp" />
                  <source srcSet="/assets/images/f8936-part4-personal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-part4-personal-img.png"
                    loading="lazy"
                    alt="Form 8936 Part IV - Credit for Previously Owned Clean Vehicles Line 14 to Line 18"
                    width={760}
                    height={114}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 14 - Previously Owned Clean Vehicle Credit Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total credit amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part IV of all applicable Schedule A (Form 8936)
                  </span>{' '}
                  forms. If you are claiming credits for more than one
                  qualifying previously owned clean vehicle, combine the amounts
                  and enter the total on this line.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 15 - Tax Liability
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 1040, Form 1040-SR, or Form 1040-NR, Line 18
                  </span>
                  . This represents your total tax liability before applying the
                  previously owned clean vehicle credit.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 16 - Personal Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total of the applicable personal credits reported on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule 3 (Form 1040), Lines 1 through 4, 5b, 6d, and 6I
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17 - Available Tax Liability
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}> Line 16 </span>{' '}
                    from <span style={{ fontWeight: 600 }}> Line 15 </span> and
                    enter the result.
                  </li>
                  <li>
                    If the result is zero or less, enter{' '}
                    <span style={{ fontWeight: 600 }}> 0 </span> and stop
                    here.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18 - Allowable Previously Owned Clean Vehicle Credit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the smaller value between{' '}
                    <span style={{ fontWeight: 600 }}> Line 14</span> and{' '}
                    <span style={{ fontWeight: 600 }}> Line 17</span>. This is
                    the amount of previously owned clean vehicle credit you can
                    claim for the tax year.{' '}
                  </li>
                  <li>
                    Also enter this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule 3 (Form 1040), Line 6m
                    </span>
                    .
                  </li>
                  <li>
                    If the amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 17</span> is less
                    than the amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 14</span>, you
                    cannot claim the excess credit. Any unused portion of the
                    previously owned clean vehicle credit is permanently lost
                    and cannot be carried back or carried forward to another tax
                    year.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part V - Credit for Qualified Commercial Clean Vehicles
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-part5-qualified-img.webp" />
                  <source srcSet="/assets/images/f8936-part5-qualified-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-part5-qualified-img.png"
                    loading="lazy"
                    alt="Form 8936 Part V - Credit for Qualified Commercial Clean Vehicles Line 19 to Line 21"
                    width={760}
                    height={81}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 19 - Qualified Commercial Clean Vehicle Credit Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total credit amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part V of all applicable Schedule A (Form 8936)
                  </span>{' '}
                  forms.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 20 - Credit From Partnerships and S Corporations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    qualified commercial clean vehicle credits
                  </span>{' '}
                  passed through to you from a partnership or S corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You can find these amounts on:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, '& li': { mb: 1.2, fontWeight: 600 } }}
                >
                  <li>Schedule K-1 (Form 1065), </li>
                  <li>Schedule K-1 (Form 1120-S) </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Partnerships and S corporations should report these
                  pass-through credits on Line 20. Other taxpayers who are also
                  reporting a separate credit on Line 19 must include these
                  amounts on Line 20 as well.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are not reporting a separate credit on Line 19, you may
                  report the pass-through credit directly on{' '}
                  <Link
                    href="/resources/tax-exempt/form-3800-business-credits/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 3800
                  </Link>
                  , <span style={{ fontWeight: 600 }}>Part III, Line 1aa</span>.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21 - Total Qualified Commercial Clean Vehicle Credit
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add <span style={{ fontWeight: 600 }}>Lines 19 and 20</span>{' '}
                  and enter the total on Line 21.
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Partnerships and S corporations
                    </span>{' '}
                    should report this amount on{' '}
                    <span style={{ fontWeight: 600 }}> Schedule K</span>.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      All other taxpayers{' '}
                    </span>{' '}
                    should report this amount on Form 3800,{' '}
                    <span style={{ fontWeight: 600 }}> Part III, Line 1aa</span>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="clean"
                >
                  Schedule A (Form 8936) - Clean Vehicle Credit Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Taxpayers must complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule A (Form 8936)
                  </span>{' '}
                  for each qualifying clean vehicle to calculate the allowable
                  credit amount. The credit calculated on Schedule A is then
                  reported on{' '}
                  <span style={{ fontWeight: 600 }}> Form 8936</span> to
                  determine the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total clean vehicle credit
                  </span>{' '}
                  that can be claimed on the tax return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="ischa"
                >
                  What Is Schedule A (Form 8936)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule A (Form 8936)
                  </span>
                  ,{' '}
                  <span style={{ fontStyle: 'italic' }}>
                    “Clean Vehicle Credit Amount”
                  </span>
                  , is used to calculate the credit amount for each qualifying
                  clean vehicle. Taxpayers must complete a separate Schedule A
                  for every vehicle for which they are claiming a credit.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whenis"
                >
                  When Is Schedule A Required?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  You must complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    a separate Schedule A (Form 8936)
                  </span>{' '}
                  for each qualifying vehicle if you are claiming:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      New Clean Vehicle Credit
                    </span>
                  </li>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Previously Owned Clean Vehicle Credit
                    </span>{' '}
                  </li>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Qualified Commercial Clean Vehicle Credit
                    </span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are claiming credits for multiple vehicles, you must
                  complete a separate Schedule A for each vehicle.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="info"
                >
                  Information Needed to Complete Schedule A
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Before completing Schedule A, gather the following
                  information:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Vehicle Identification Number (VIN)</li>
                  <li>Vehicle make and model </li>
                  <li>Date the vehicle was placed in service </li>
                  <li>Date of purchase </li>
                  <li>Credit transfer information, if applicable </li>
                  <li>Business-use percentage (for business vehicles) </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="scha"
                >
                  Schedule A (Form 8936) - Instructions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-clean-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-clean-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-clean-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) - Instructions, Organization details"
                    width={760}
                    height={159}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before completing the credit calculation sections, enter the
                  required taxpayer's information at the top of Schedule A.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Name(s) Shown on Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name exactly as it appears on the federal tax return
                  to which Schedule A (Form 8936) is attached.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Identifying Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the identifying number shown on your tax return, such as
                  a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Social Security Number (SSN)
                  </span>{' '}
                  or{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Employer Identification Number (EIN)
                  </span>
                  , as applicable.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> A separate Schedule A (Form 8936) must be
                  completed for each qualifying clean vehicle, and individuals
                  who transferred the credit to a dealer at the time of sale
                  must file both Schedule A and Form 8936 with their tax return.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part I - Vehicle Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part1-vehicle-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part1-vehicle-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part1-vehicle-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part I - Vehicle Details Line 1a to Line 7"
                    width={760}
                    height={436}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1a - Vehicle Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the vehicle's model year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1b - Vehicle Make
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the manufacturer or brand of the vehicle{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1c - Vehicle Model
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the specific model of the vehicle for which you are
                  claiming the credit.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Vehicle Identification Number (VIN)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Vehicle Identification Number (VIN)
                    </span>
                    . The VIN is a unique identifier generally consisting of 17
                    characters made up of letters and numbers.
                  </li>
                  <li>
                    You can find the VIN on the{' '}
                    <span style={{ fontWeight: 600 }}>
                      vehicle registration
                    </span>{' '}
                    , title, insurance documents, or the vehicle itself.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3 - Date Vehicle Was Placed in Service
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the date the vehicle was placed in service using the
                    <span style={{ fontWeight: 600 }}> MM/DD/YYYY</span>{' '}
                    format.{' '}
                  </li>
                  <li>
                    Generally, a vehicle is considered placed in service on the
                    date you take possession of it and begin using it.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4a - Credit Transfer Election
                </Typography>
                <Typography variant="body1">
                  Indicate whether you transferred the clean vehicle credit to
                  the dealer at the time of sale.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> enter the
                    credit amount transferred to the dealer as shown on the
                    seller's report.{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> proceed to
                    Line 5{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4b - Credit Transfer Reporting
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you transferred your clean vehicle credit to the dealer
                    at the time of sale, select "Yes" and enter the credit
                    amount shown on the seller's report.
                  </li>
                  <li>
                    If required by{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Line 8a, 8d, 13a, or 13c,
                    </span>{' '}
                    report the transferred credit amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule 2 (Form 1040), Line 1b
                    </span>
                    .
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5 - New Clean Vehicle Eligibility
                </Typography>
                <Typography variant="body1">
                  Indicate whether the VIN entered on Line 2 belongs to a{' '}
                  <span style={{ fontWeight: 600 }}> new clean vehicle</span>{' '}
                  that was acquired before{' '}
                  <span style={{ fontWeight: 600 }}> October 1, 2025,</span> and
                  placed in service during the tax year.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> proceed
                    to Part II.{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> continue
                    to Line 6.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <span style={{ fontWeight: 600 }}>Tips:</span> If the vehicle
                  qualifies for both the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    New Clean Vehicle Credit
                  </span>{' '}
                  and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Qualified Commercial Clean Vehicle Credit,
                  </span>{' '}
                  you may choose which credit to claim.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6 - Previously Owned Clean Vehicle Eligibility
                </Typography>
                <Typography variant="body1">
                  Indicate whether the VIN entered on Line 2 belongs to a
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    previously owned clean vehicle
                  </span>{' '}
                  acquired after 2022 and before{' '}
                  <span style={{ fontWeight: 600 }}>October 1, 2025,</span> and
                  placed in service during the tax year.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> proceed
                    to <span style={{ fontWeight: 600 }}> Part IV</span>.
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> continue
                    to Line 7.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7 - Qualified Commercial Clean Vehicle Eligibility
                </Typography>
                <Typography variant="body1">
                  Indicate whether the VIN entered on Line 2 belongs to a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    qualified commercial clean vehicle
                  </span>{' '}
                  acquired after 2022 and before{' '}
                  <span style={{ fontWeight: 600 }}> October 1, 2025,</span> and
                  placed in service during the tax year.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> proceed
                    to <span style={{ fontWeight: 600 }}> Part V</span>.
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> stop here.
                    The vehicle does not qualify for a credit under Schedule A
                    (Form 8936).
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <span style={{ fontWeight: 600 }}>Tips:</span> If the vehicle
                  qualifies for both the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    ualified Commercial Clean Vehicle Credit
                  </span>{' '}
                  and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    New Clean Vehicle Credit,
                  </span>{' '}
                  you may choose which credit to claim.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part II - Credit Amount for Business/Investment Use Part of
                  New Clean Vehicle
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part2-credit-amount-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part2-credit-amount-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part2-credit-amount-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part II - Credit Amount for Business/Investment Use Part of New Clean Vehicle Line 8a to Line 8d"
                    width={760}
                    height={336}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8a - Vehicle Resold Within 30 Days
                </Typography>
                <Typography variant="body1">
                  Indicate whether you resold the vehicle within 30 days of the
                  placed-in-service date entered on Line 3.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}> “Yes,”</span> you are
                    not eligible to claim a clean vehicle credit for this
                    vehicle. If you transferred the credit to the dealer at the
                    time of sale, check the box on{' '}
                    <span style={{ fontWeight: 600 }}> Line 4b</span> and report
                    the transferred credit amount from{' '}
                    <span style={{ fontWeight: 600 }}> Line 4a</span> on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Schedule 2 (Form 1040), Line 1b
                    </span>
                    .
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}> “No,”</span> continue
                    to Line 8b.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8b - Type of Tax Return
                </Typography>
                <Typography variant="body1">
                  Indicate whether you are filing{' '}
                  <span style={{ fontWeight: 600 }}> Form 8936</span> with an
                  individual income tax return.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}> “Yes,”</span> continue
                    to Line 8c.
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}> “No,”</span> skip
                    Lines 8c and 8d and proceed to Line 8e.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8c - Current-Year MAGI Limitation
                </Typography>
                <Typography variant="body1">
                  Complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8936, Lines 1 and 2
                  </span>
                  , and compare your current-year Modified Adjusted Gross Income
                  (MAGI) with the applicable income limit shown in the chart
                  below Line 5 of Form 8936.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If your MAGI exceeds the applicable limit, continue to Line
                    8d.{' '}
                  </li>
                  <li>
                    If your MAGI is within the applicable limit, proceed to Line
                    8e unless you transferred the credit to the dealer, in which
                    case follow the instructions provided on the form.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8d - Prior-Year MAGI Limitation
                </Typography>
                <Typography variant="body1">
                  Complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8936, Lines 3, 4, and 5,
                  </span>{' '}
                  and compare your prior-year Modified Adjusted Gross Income
                  (MAGI) with the applicable income limit for your 2024 filing
                  status.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If your prior-year MAGI exceeds the applicable limit, you
                    cannot claim a clean vehicle credit for this vehicle. If you
                    transferred the credit to the dealer at the time of sale,
                    check the box on{' '}
                    <span style={{ fontWeight: 600 }}> Line 4b</span> and report
                    the transferred credit amount from{' '}
                    <span style={{ fontWeight: 600 }}> Line 4a</span> on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule 2 (Form 1040), Line 1b.
                    </span>{' '}
                  </li>
                  <li>
                    If your prior-year MAGI is within the applicable limit,
                    continue to{' '}
                    <span style={{ fontWeight: 600 }}> Line 8e</span>. However,
                    if you transferred the credit to the dealer at the time of
                    sale, follow the instructions provided on the form before
                    proceeding.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part2-credit-line8-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part2-credit-line8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part2-credit-line8-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part II - Credit Amount for Business/Investment Use Part of New Clean Vehicle Line 8e to Line 11"
                    width={760}
                    height={202}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8e - Vehicle Use Requirement
                </Typography>
                <Typography variant="body1">
                  Indicate whether you acquired the vehicle for your own use or
                  to lease to others.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"Yes"</span> if you
                    purchased the vehicle for business use or for leasing
                    purposes.{' '}
                  </li>
                  <li>
                    Select <span style={{ fontWeight: 600 }}> "No"</span> if you
                    acquired the vehicle for resale or are leasing it from
                    another person.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you answer <span style={{ fontWeight: 600 }}> "No," </span>{' '}
                  you are not eligible to claim a clean vehicle credit for this
                  vehicle and should stop here
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Tentative Credit Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the tentative credit amount provided by the seller at
                  the time of purchase. This amount is typically listed on the
                  seller's report and generally represents the maximum clean
                  vehicle credit available for the vehicle.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Business/Investment Use Percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the percentage of the vehicle's use that is attributable
                  to business or investment purposes.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}> 100%</span> if the
                    vehicle is used exclusively for business purposes.
                  </li>
                  <li>
                    If the vehicle is used for both business and personal
                    purposes, calculate the business-use percentage by dividing
                    the miles driven for business or income-producing activities
                    (excluding commuting miles) by the total miles driven during
                    the year.
                  </li>
                  <li>
                    If employees use the vehicle and the value of any personal
                    use is included in their taxable income or reimbursed to
                    you, the vehicle may be treated as{' '}
                    <span style={{ fontWeight: 600 }}> 100% business use</span>.
                  </li>
                  <li>
                    If the vehicle's use changed during the year from personal
                    to business use (or vice versa), calculate the percentage
                    based only on the period it was used for business or
                    investment purposes.
                  </li>
                </Box>{' '}
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11 - Business/Investment Use Credit Amount
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Multiply <span style={{ fontWeight: 600 }}> Line 9</span> by{' '}
                    <span style={{ fontWeight: 600 }}> Line 10</span> and enter
                    the result on Line 11. Include this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 8936, Part II, Line 6
                    </span>
                    .
                  </li>
                  <li>
                    If you enter <span style={{ fontWeight: 600 }}> 100%</span>{' '}
                    on Line 10, stop here. Otherwise, continue to{' '}
                    <span style={{ fontWeight: 600 }}> Part III</span> to
                    determine the personal use portion of the credit, if
                    applicable.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part III - Credit Amount for Personal Use Part of New Clean
                  Vehicle
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part3-credit-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part3-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part3-credit-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part III - Credit Amount for Personal Use Part of New Clean Vehicle Line 12"
                    width={760}
                    height={49}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 12 - Personal Use Portion of Credit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}> Line 11</span>{' '}
                    from <span style={{ fontWeight: 600 }}> Line 9</span> and
                    enter the result on Line 12.{' '}
                  </li>
                  <li>
                    Enter this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 8936, Part III, Line 9
                    </span>
                    . If there is no remaining personal-use portion, no further
                    calculation is required.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IV - Credit Amount for Previously Owned Clean Vehicle
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part4-credit-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part4-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part4-credit-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part IV - Credit Amount for Previously Owned Clean Vehicle Line 13a to Line 13g"
                    width={759}
                    height={526}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13a - Vehicle Resold Within 30 Days
                </Typography>
                <Typography variant="body1">
                  Indicate whether you resold the vehicle within 30 days of the
                  placed-in-service date entered on Line 3.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> you are
                    not eligible to claim a clean vehicle credit for this
                    vehicle. If you transferred the credit to the dealer at the
                    time of sale, check the box on Line 4b and report the
                    transferred credit amount from{' '}
                    <span style={{ fontWeight: 600 }}> Line 4a </span> on
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule 2 (Form 1040), Line 1c
                    </span>{' '}
                    .{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> continue
                    to <span style={{ fontWeight: 600 }}> Line 13b</span>.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13b - Current-Year MAGI Limitation
                </Typography>
                <Typography variant="body1">
                  Complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8936, Lines 1 and 2,
                  </span>{' '}
                  and compare your current-year Modified Adjusted Gross Income
                  (MAGI) with the applicable income limit for your 2025 filing
                  status shown in the chart below Line 5 of Form 8936.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your MAGI exceeds the applicable limit, continue to{' '}
                    <span style={{ fontWeight: 600 }}> Line 13c</span>.
                  </li>
                  <li>
                    If your MAGI is within the applicable limit, proceed to{' '}
                    <span style={{ fontWeight: 600 }}> Line 13d</span>. However,
                    if you transferred the credit to the dealer at the time of
                    sale, follow the instructions provided on the form.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13c - Prior-Year MAGI Limitation
                </Typography>
                <Typography variant="body1">
                  Complete{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8936, Lines 3, 4, and 5,
                  </span>{' '}
                  and compare your prior-year MAGI with the applicable income
                  limit for your 2024 filing status.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your prior-year MAGI exceeds the applicable limit, you
                    cannot claim a clean vehicle credit for this vehicle. If you
                    transferred the credit to the dealer at the time of sale,
                    check the box on{' '}
                    <span style={{ fontWeight: 600 }}> Line 4b</span> and report
                    the transferred credit amount from
                    <span style={{ fontWeight: 600 }}> Line 4a</span> on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Schedule 2 (Form 1040), Line 1c
                    </span>
                    .{' '}
                  </li>
                  <li>
                    If your prior-year MAGI is within the applicable limit,
                    continue to{' '}
                    <span style={{ fontWeight: 600 }}> Line 13d</span>. However,
                    if you transferred the credit to the dealer at the time of
                    sale, follow the instructions provided on the form.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13d - Previously Owned Vehicle Credit Claimed Within the
                  Last Three Years
                </Typography>
                <Typography variant="body1">
                  Indicate whether you claimed a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Previously Owned Clean Vehicle Credit
                  </span>{' '}
                  for another vehicle purchased during the three-year period
                  ending on the date you purchased the vehicle listed in Part I.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> you
                    cannot claim the credit for this vehicle.{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> continue
                    to Line 13e.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13e - Vehicle Sales Price Test
                </Typography>
                <Typography variant="body1">
                  Determine whether the vehicle's sales price exceeds{' '}
                  <span style={{ fontWeight: 600 }}>$25,000</span>.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> the
                    vehicle does not qualify for the Previously Owned Clean
                    Vehicle Credit.{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> continue
                    to <span style={{ fontWeight: 600 }}> Line 13f</span>.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13f - Vehicle Use Requirement
                </Typography>
                <Typography variant="body1">
                  Indicate whether you acquired the vehicle for your own use and
                  not for resale.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"Yes"</span> if you
                    purchased the vehicle for personal or business use.{' '}
                  </li>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"No"</span> if you
                    acquired the vehicle for resale or if you are leasing the
                    vehicle from another person.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you answer <span style={{ fontWeight: 600 }}>"No,"</span>{' '}
                  you are not eligible to claim the credit and should stop here.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13g - Dependent Status
                </Typography>
                <Typography variant="body1">
                  Indicate whether you can be claimed as a dependent on another
                  person's tax return.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“Yes,”</span> you are
                    not eligible to claim the Previously Owned Clean Vehicle
                    Credit.{' '}
                  </li>
                  <li>
                    If <span style={{ fontWeight: 600 }}>“No,”</span> continue
                    to the next applicable line to determine your allowable
                    credit amount.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part4-credit-line14-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part4-credit-line14-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part4-credit-line14-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part IV - Credit Amount for Previously Owned Clean Vehicle Line 14 to Line 17"
                    width={760}
                    height={124}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 14 - Vehicle Sales Price
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the sales price of the previously owned clean
                  vehicle.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 15 - Calculate 30% of the Sales Price
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the amount on{' '}
                  <span style={{ fontWeight: 600 }}> Line 14</span> by{' '}
                  <span style={{ fontWeight: 600 }}> 30% (0.30)</span> and enter
                  the result.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 16 - Maximum Vehicle Credit Amount
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The maximum credit allowed for a previously owned clean
                    vehicle is <span style={{ fontWeight: 600 }}>$4,000</span>
                    .{' '}
                  </li>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}>$4,000</span> on
                    this line{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17 - Allowable Previously Owned Clean Vehicle Credit
                </Typography>
                <Typography variant="body1">Enter the smaller of:</Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 15</span>, or{' '}
                    <span style={{ fontWeight: 600 }}> Line 16</span>.{' '}
                  </li>
                  <li>
                    This is your allowable{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Previously Owned Clean Vehicle Credit
                    </span>
                    . Enter this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 8936, Part IV, Line 14
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part V - Credit Amount for Qualified Commercial Clean Vehicle
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part5-credit-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part5-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part5-credit-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part V - Credit Amount for Qualified Commercial Clean Vehicle  Line 18a to Line 18e"
                    width={760}
                    height={281}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18a – Elective Payment Election Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS-issued registration number
                  </span>{' '}
                  for the qualified commercial clean vehicle, if
                  applicable.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18b - Depreciation Requirement
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Indicate whether the vehicle is subject to a depreciation
                  allowance.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"Yes"</span> if the
                    vehicle qualifies for depreciation.
                  </li>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"No"</span> if the
                    vehicle does not qualify for depreciation.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you answer <span style={{ fontWeight: 600 }}>"No,"</span>{' '}
                  the vehicle generally does not qualify as a commercial clean
                  vehicle unless it meets the exception available for certain
                  tax-exempt and governmental entities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18c - Vehicle Use Requirement
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Indicate whether you acquired the vehicle for use in your
                  trade or business, or to lease to others, and not for resale.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}> "Yes"</span> if
                    the vehicle was acquired for business use or leasing
                    purposes.{' '}
                  </li>
                  <li>
                    Select <span style={{ fontWeight: 600 }}> "No" </span> if
                    the vehicle was acquired for resale or if you are leasing
                    the vehicle from another person.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you answer <span style={{ fontWeight: 600 }}> "No,"</span>{' '}
                  you are not eligible to claim the credit for this vehicle.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18d - Vehicle Power Source
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Indicate whether the vehicle is powered in part by a gasoline
                  or diesel engine.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"Yes"</span> if the
                    vehicle is powered by both an electric motor and a gasoline
                    or diesel engine.
                  </li>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>"No"</span> if the
                    vehicle is not powered by a gasoline or diesel engine.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This determination affects the credit calculation. Vehicles
                  powered in part by a gasoline or diesel engine may qualify for
                  the credit, but the applicable credit rate is generally
                  reduced from{' '}
                  <span style={{ fontWeight: 600 }}> 30% to 15%</span>.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18e - Gross Vehicle Weight Rating (GVWR)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the vehicle's{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Gross Vehicle Weight Rating (GVWR)
                  </span>
                  .{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8936-scha-part5-credit-line19-img.webp" />
                  <source srcSet="/assets/images/f8936-scha-part5-credit-line19-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8936-scha-part5-credit-line19-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8936) Part V - Credit Amount for Qualified Commercial Clean Vehicle  Line 19 to Line 26"
                    width={760}
                    height={281}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 19 - Cost or Other Basis of the Vehicle
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the vehicle's{' '}
                  <span style={{ fontWeight: 600 }}> cost or other basis</span>.
                  This is generally the amount paid to acquire the vehicle,
                  including certain purchase-related costs. The amount entered
                  on this line is used to calculate the qualified commercial
                  clean vehicle credit.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 20 - Section 179 Expense Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Section 179 expense deduction
                  </span>{' '}
                  claimed for the vehicle from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part I of{' '}
                    <Link
                      href="/resources/tax-exempt/form-4562-depreciation-amortization-guide/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 4562
                    </Link>
                    , Depreciation and Amortization
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21 – Adjusted Vehicle Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}> Line 20</span>{' '}
                  from <span style={{ fontWeight: 600 }}> Line 19</span> and
                  enter the result.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 22 - Credit Calculation
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Calculate the tentative credit amount by multiplying{' '}
                  <span style={{ fontWeight: 600 }}> Line 21</span> by the
                  applicable percentage:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Multiply by{' '}
                    <span style={{ fontWeight: 600 }}>15% (0.15)</span> if you
                    have answered <span style={{ fontWeight: 600 }}>"Yes"</span>{' '}
                    on Line 18d.
                  </li>
                  <li>
                    Multiply by{' '}
                    <span style={{ fontWeight: 600 }}>30% (0.30)</span> if you
                    have answered <span style={{ fontWeight: 600 }}>"No"</span>{' '}
                    on Line 18d.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23 - Incremental Cost
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the vehicle's{' '}
                    <span style={{ fontWeight: 600 }}> incremental cost,</span>{' '}
                    if applicable.
                  </li>
                  <li>
                    Generally, incremental cost is the excess cost of the clean
                    vehicle compared to a similar vehicle powered solely by a
                    gasoline or diesel internal combustion engine.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 24 - Smaller of Line 22 or Line 23
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the smaller of:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 22</span>, or{' '}
                  </li>
                  <li>
                    The amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 23 </span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This amount is used to determine the allowable credit before
                  applying the maximum credit limitation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 25 - Maximum Credit Amount
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the applicable maximum credit amount:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> $7,500</span> for
                    vehicles with a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Gross Vehicle Weight Rating (GVWR)
                    </span>{' '}
                    of less than{' '}
                    <span style={{ fontWeight: 600 }}> 14,000 pounds,</span> or
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> $40,000</span> for
                    vehicles with a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      GVWR of 14,000 pounds or more
                    </span>
                    .{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 26 - Allowable Qualified Commercial Clean Vehicle Credit
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the smaller of:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 24</span>, or
                  </li>
                  <li>
                    The amount on{' '}
                    <span style={{ fontWeight: 600 }}> Line 25</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This is your allowable{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Qualified Commercial Clean Vehicle Credit
                  </span>
                  . Enter this amount on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8936, Part V, Line 19
                  </span>
                  .
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                      scrollMarginTop: 88,
                    }}
                    id="add"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                        <ul>
                          <li style={{ marginBottom: 8 }}>
                            {' '}
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i8936.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Instructions
                            </Link>{' '}
                            for Form 8936 – Clean Vehicle Credits{' '}
                          </li>

                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/instructions/i3800"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Instructions
                            </Link>{' '}
                            for Form 3800 – General Business Credit{' '}
                          </li>
                          <li style={{ marginBottom: 8 }}>
                            <Link
                              href="https://www.irs.gov/instructions/i4562"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Instructions
                            </Link>{' '}
                            for Form 4562 – Depreciation and Amortization{' '}
                          </li>
                          <li style={{ marginBottom: 0 }}>
                            <Link
                              href="https://www.irs.gov/publications/p551"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Publication 551
                            </Link>{' '}
                            – Basis of Assets{' '}
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" id="common" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the IRS pre-filing registration requirement for the
                  Qualified Commercial Clean Vehicle Credit?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Tax-exempt and governmental entities that plan to elect
                    payment of the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Qualified Commercial Clean Vehicle Credit
                    </span>{' '}
                    must complete the IRS pre-filing registration process before
                    filing their tax return. Once the registration is approved,
                    the IRS issues a registration number that must be included
                    when claiming the credit. This process helps the IRS verify
                    eligibility and process the elective payment request.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is a seller's report for the clean vehicle credit?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    A seller's report is a document provided by the dealer at
                    the time of sale that contains information about the vehicle
                    and the applicable clean vehicle credit. Taxpayers may need
                    information from the seller's report when completing
                    Schedule A (Form 8936) and Form 8936, including the
                    tentative credit amount and any credit transferred to the
                    dealer.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. How much credit amount can I claim with Form 8936?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    The credit amount varies based on the vehicle’s battery
                    capacity and compliance with federal guidelines, typically
                    up to $7,500 ($40,000 for a vehicle with a GVWR of 14,000
                    pounds or more).
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Can I claim the Clean Vehicle Credit for a leased vehicle?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      Yes, if you lease a qualifying electric vehicle, the
                      lessor may pass the credit on to you as part of the lease
                      agreement. Confirm with the lessor if you qualify for the
                      credit.
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mb: 0,
                        mt: 0,
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <b> Note:</b> If the vehicle is leased, only the lessor
                      and not the lessee is entitled to the credit.
                    </Alert>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. What documents do I need to file Form 8936?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    You will need to provide documentation such as the purchase
                    agreement, vehicle identification number (VIN), and proof of
                    battery capacity.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form8936;
